// @flow weak

import _ from 'lodash';

import { Megaphone } from '@zapier/perf-utils';

import { isProductionEnv } from 'app/common/CommonUtils';

const getReducerName = () => {
  const stack = new Error().stack;
  // In webpack, each module has a `parents`
  // property for all the modules which
  // import it. Flow doesn't know that. $FlowFixMe
  return (module.parents || []).find(parent =>
    stack.includes(parent) ? parent : null
  );
};

const warnOnReferenceOnlyStateChanges = isProductionEnv()
  ? _.noop
  : (oldState, newState, reducerName, actionType) => {
      const isReferenceOnlyStateChange =
        newState !== oldState && _.isEqual(newState, oldState);
      const reducerLabel = _.isEmpty(reducerName)
        ? 'a reducer'
        : `${reducerName} reducer`;
      if (isReferenceOnlyStateChange) {
        Megaphone.alert({
          message: `
          Reference-only change in ${reducerLabel} when handling ${actionType}.
          This may cause needless re-rendering, thus degrading the user experience.`,
          stack: new Error().stack,
        });
      }
    };

export const createReducer = (handlers, initialState) => {
  const reducerName = getReducerName();
  return (state = initialState, action) => {
    let newState;
    if (handlers[action.type]) {
      newState = handlers[action.type](state, action);
    }
    if (newState) {
      warnOnReferenceOnlyStateChanges(
        state,
        newState,
        reducerName,
        action.type
      );
      return newState;
    }
    return state;
  };
};

export const waitForStoreState = (store, predicate) => {
  // Maybe store is already in the desired state.
  try {
    if (predicate(store.getState())) {
      return Promise.resolve();
    }
  } catch (err) {
    return Promise.reject(err);
  }

  // Otherwise, subscribe and wait for it.
  return new Promise(resolve => {
    const unsubscribe = store.subscribe(() => {
      if (predicate(store.getState())) {
        unsubscribe();
        resolve();
      }
    });
  });
};

export const maybeWaitForStoreStateWithActions = (store, configs) => {
  // Go ahead and fire all the actions.
  configs.forEach(config => {
    if (_.isUndefined(config.test) || !config.test(store.getState())) {
      if (_.isFunction(config.action)) {
        if (config.after) {
          waitForStoreState(store, config.after).then(() => {
            store.dispatch(config.action(store.getState()));
          });
        } else {
          store.dispatch(config.action(store.getState()));
        }
      }
    }
  });

  // Grab the configs we have to wait on.
  const waitConfigs = configs.filter(
    config => config.shouldWait && _.isFunction(config.test)
  );
  const cancelConfigs = configs.filter(
    config => config.shouldCancel && _.isFunction(config.test)
  );

  // If none of those, we're done.
  if (waitConfigs.length === 0) {
    return Promise.resolve();
  }

  const predicate = state => {
    return (
      cancelConfigs.some(config => config.test(state)) ||
      waitConfigs.every(config => config.test(state))
    );
  };

  return waitForStoreState(store, predicate);
};

/*
   Returns a promise that resolves when a set of actions has completed.

   Example: waitForActions(store, ['LOAD_FOO_DONE', 'LOAD_BAR_DONE'])
   -- will resolve when LOAD_FOO_DONE and LOAD_BAR_DONE have both
      been dispatched since this waitForActions call.

   Example: waitForActions(store, {LOAD_FOO_DONE: 1, LOAD_BAR_DONE: 2})
   -- will resolve when LOAD_FOO_DONE has been dispatched at least once,
      and LOAD_BAR_DONE has been dispatched at least twice,
      since this waitForActions call

   Promise will be rejected if any '*_FAIL' actions have been dispatched
   since this waitForActions call.
*/

// Leaving this here just for reference. Should bury this in middleware if we
// really want to do this.
/*
const waitForActions = (store, expectedActions) => {
  const initialHistory = store.getState().actionHistory;

  if (Array.isArray(expectedActions)) {
    expectedActions = _.countBy(expectedActions, actionType => actionType);
  }

  return new Promise((resolve, reject) => {
    if (_.isEmpty(expectedActions)) {
      return resolve();
    }

    const unsubscribe = store.subscribe(() => {
      const historySnapshot = store.getState().actionHistory;

      const historySince = Object.keys(historySnapshot).reduce((counts, actionType) => {
        counts[actionType] = historySnapshot[actionType] - (initialHistory[actionType] || 0);
        return counts;
      }, {});

      const failures = _.filter(Object.keys(historySince), actionType => actionType.match(/_FAIL$/));
      if (failures.length > 0) {
        console.error('action failures', failures);
        unsubscribe();
        return reject(new Error('action failures'));
      }

      const isStillWaiting = _.some(Object.keys(expectedActions), actionType =>
        (expectedActions[actionType] - (historySince[actionType] || 0)) > 0
      );

      if (!isStillWaiting) {
        unsubscribe();
        resolve();
      }
    });
  });
};
*/
